/**
 * useTenantForm Hook
 *
 * Manages tenant form state, validation, and initialization for create/edit modes.
 *
 * @module features/tenants/hooks/useTenantForm
 */

import { useState, useCallback } from 'react';
import type { Tenant, TenantFormData, FormErrors, NavigationOverrides } from '../types';
import { defaultFormData } from '../utils/constants';
import { validateTenantForm, hasErrors } from '../utils/validation';

/**
 * Return type for useTenantForm hook.
 */
export interface UseTenantFormResult {
  /** Current form data */
  formData: TenantFormData;
  /** Validation errors by field */
  formErrors: FormErrors;
  /** Whether form is being submitted */
  isSaving: boolean;
  /** Modal-level error message */
  modalError: string | null;

  /** Update the entire form data */
  setFormData: (data: TenantFormData) => void;
  /** Update a single form field */
  setField: <K extends keyof TenantFormData>(field: K, value: TenantFormData[K]) => void;
  /** Update form errors */
  setFormErrors: (errors: FormErrors) => void;
  /** Clear a single error */
  clearFieldError: (field: string) => void;
  /** Set saving state */
  setIsSaving: (value: boolean) => void;
  /** Set modal error */
  setModalError: (error: string | null) => void;

  /** Validate form and return errors */
  validate: (isEdit: boolean) => FormErrors;
  /** Validate and check if form has errors */
  validateAndCheck: (isEdit: boolean) => boolean;
  /** Reset form to defaults */
  reset: () => void;
  /** Initialize form for editing a tenant */
  initializeForEdit: (tenant: Tenant) => void;
}

/**
 * Hook for managing tenant form state and validation.
 *
 * @returns Form state, setters, and utility functions
 *
 * @example
 * ```tsx
 * const {
 *   formData,
 *   formErrors,
 *   isSaving,
 *   setField,
 *   validate,
 *   reset,
 *   initializeForEdit,
 * } = useTenantForm();
 *
 * // Update a field
 * setField('name', 'Acme Corp');
 *
 * // Validate before submit
 * const errors = validate(false);
 * if (!hasErrors(errors)) {
 *   await submitForm();
 * }
 * ```
 */
export const useTenantForm = (): UseTenantFormResult => {
  const [formData, setFormDataState] = useState<TenantFormData>(defaultFormData);
  const [formErrors, setFormErrorsState] = useState<FormErrors>({});
  const [isSaving, setIsSaving] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  /**
   * Set the entire form data.
   */
  const setFormData = useCallback((data: TenantFormData) => {
    setFormDataState(data);
  }, []);

  /**
   * Update a single form field.
   */
  const setField = useCallback(<K extends keyof TenantFormData>(
    field: K,
    value: TenantFormData[K]
  ) => {
    setFormDataState(prev => ({ ...prev, [field]: value }));
  }, []);

  /**
   * Set form errors.
   */
  const setFormErrors = useCallback((errors: FormErrors) => {
    setFormErrorsState(errors);
  }, []);

  /**
   * Clear a single field error.
   */
  const clearFieldError = useCallback((field: string) => {
    setFormErrorsState(prev => {
      if (!(field in prev)) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  /**
   * Validate form and return errors.
   */
  const validate = useCallback((isEdit: boolean): FormErrors => {
    const errors = validateTenantForm(formData, isEdit);
    setFormErrorsState(errors);
    return errors;
  }, [formData]);

  /**
   * Validate and return true if form has errors.
   */
  const validateAndCheck = useCallback((isEdit: boolean): boolean => {
    const errors = validateTenantForm(formData, isEdit);
    setFormErrorsState(errors);
    return hasErrors(errors);
  }, [formData]);

  /**
   * Reset form to defaults.
   */
  const reset = useCallback(() => {
    setFormDataState(defaultFormData);
    setFormErrorsState({});
    setModalError(null);
    setIsSaving(false);
  }, []);

  /**
   * Initialize form for editing a tenant.
   */
  const initializeForEdit = useCallback((tenant: Tenant) => {
    const navOverrides = tenant.navigation_overrides as unknown as NavigationOverrides | undefined;

    setFormDataState({
      slug: tenant.slug,
      name: tenant.name,
      admin_email: tenant.admin_email,
      admin_name: tenant.admin_name || '',
      isolation_level: tenant.isolation_level,
      template_slug: '',
      settings: {
        timezone: tenant.settings?.timezone || 'UTC',
        dateFormat: tenant.settings?.dateFormat || 'YYYY-MM-DD',
        locale: tenant.settings?.locale || 'en-US',
      },
      branding: {
        primaryColor: tenant.branding?.primaryColor || '#7c3aed',
        secondaryColor: tenant.branding?.secondaryColor || '#06b6d4',
        logo: tenant.branding?.logo || '',
        favicon: tenant.branding?.favicon || '',
        appName: tenant.branding?.appName || '',
      },
      limits: {
        maxUsers: tenant.limits?.maxUsers || 100,
        maxStorageGB: tenant.limits?.maxStorageGB || 50,
        maxApiCallsPerDay: tenant.limits?.maxApiCallsPerDay || 10000,
      },
      ai_config: {
        enabled: tenant.ai_config?.enabled ?? true,
        modelsAllowed: tenant.ai_config?.modelsAllowed || ['claude-3-5-sonnet'],
        dailyTokenLimit: tenant.ai_config?.dailyTokenLimit || 100000,
        customGuidelines: tenant.ai_config?.customGuidelines || '',
      },
      features: tenant.features || [],
      vertical: tenant.vertical || '',
      navigation_overrides: {
        hiddenItems: navOverrides?.hiddenItems || [],
        hiddenGroups: navOverrides?.hiddenGroups || [],
        defaultTab: navOverrides?.defaultTab || 'dashboard',
      },
    });
    setFormErrorsState({});
    setModalError(null);
  }, []);

  return {
    formData,
    formErrors,
    isSaving,
    modalError,
    setFormData,
    setField,
    setFormErrors,
    clearFieldError,
    setIsSaving,
    setModalError,
    validate,
    validateAndCheck,
    reset,
    initializeForEdit,
  };
};
